import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';

import { WorkspaceSwatch } from '../side-bar/WorkspaceSwitcher';
import { workspaceById } from '../side-bar/workspaces';
import { useWorkspaceStore } from '../side-bar/workspace.store';
import { Goal, GOALS } from './journey';

/** The one thing to do first in each workspace, and where it is. */
export const FIRST_STEP: Record<Goal, string> = {
    visualizer: '/visualizer/settings?tab=button',
    assistant: '/assistant/settings',
    marketing: '/marketing/content-studio',
    business: '/business/dashboard',
    ads: '/ads/dashboard',
};

const KEY = 'ardealy.bo.start-here.dismissed';

export const isStartHereDismissed = (): boolean => {
    try {
        return localStorage.getItem(KEY) === '1';
    } catch {
        return false;
    }
};

/**
 * Right after the journey: the workspaces the vendor chose, each with the
 * first thing to do in it, on Home — so the dashboard they land on opens
 * onto what they said they came for, in their order. Dismissed once, gone.
 */
/** The chosen workspaces Home should open onto, or none once dismissed. */
export function useStartHere() {
    const goals = useWorkspaceStore((s) => s.goals).filter((g): g is Goal => (GOALS as readonly string[]).includes(g));
    const [hidden, setHidden] = useState(isStartHereDismissed);
    const hide = () => {
        try {
            localStorage.setItem(KEY, '1');
        } catch {
            /* hidden for this visit only */
        }
        setHidden(true);
    };
    return { visible: !hidden && goals.length > 0, goals, hide };
}

export const StartHere: React.FC<{ goals: Goal[]; onHide: () => void }> = ({ goals, onHide }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const close = onHide;

    return (
        <section className="mx-auto w-full max-w-4xl" data-testid="start-here">
            <div className="mb-2.5 flex items-center justify-between gap-3">
                <h2 className="text-sm font-medium">{t('ONBOARDING_V2.START_HERE.TITLE')}</h2>
                <button type="button" onClick={close} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                    <X className="size-3.5" />
                    {t('ONBOARDING_V2.START_HERE.HIDE')}
                </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
                {goals.slice(0, 3).map((goal, i) => {
                    const ws = workspaceById(goal);
                    return (
                        <motion.button
                            key={goal}
                            type="button"
                            onClick={() => navigate(FIRST_STEP[goal])}
                            className="group flex flex-col gap-2 rounded-2xl border bg-card p-4 text-start transition-colors hover:border-foreground/25 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 + i * 0.08 }}
                        >
                            <span className="flex items-center gap-2">
                                <WorkspaceSwatch hue={ws.hue} className="size-6 rounded-md" />
                                <span className="text-xs text-muted-foreground">{t(ws.labelKey, ws.label)}</span>
                                <span className="ms-auto text-[11px] text-muted-foreground tabular-nums">{i + 1}</span>
                            </span>
                            <span className="text-sm font-medium leading-snug">{t(`ONBOARDING_V2.START_HERE.${goal.toUpperCase()}`)}</span>
                            <span className="text-xs leading-relaxed text-muted-foreground">{t(`ONBOARDING_V2.START_HERE.${goal.toUpperCase()}_BODY`)}</span>
                            <span className="mt-auto inline-flex items-center gap-1 pt-1 text-xs font-medium">
                                {t('ONBOARDING_V2.START_HERE.GO')}
                                <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5" />
                            </span>
                        </motion.button>
                    );
                })}
            </div>
        </section>
    );
};
